import { faLock, faLockOpen } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Stack } from '@sixui/ui';
import { colorSchemeTokens } from '@sixui/ui/themes/base/colorScheme.stylex';
import stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IFeatureLimit } from '~/lib/api/modules/Apps';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { useAppCurrentFeaturePlanQuery } from '~/lib/api/modules/Apps/hooks/useAppCurrentFeaturePlanQuery';

export type IAppFeaturePlanProps = IContainerProps & {
  appId: string;
};

const styles = stylex.create({
  locked: {
    color: colorSchemeTokens.error,
  },
  unlocked: {
    color: 'green',
  },
});

const getFormattedLimit = (limit: IFeatureLimit): string | undefined => {
  if (limit.maxCount !== undefined) {
    if (limit.currentUsage !== undefined) {
      return `${limit.currentUsage} / ${limit.maxCount}`;
    } else {
      return `${limit.maxCount}`;
    }
  } else {
    if (limit.currentUsage !== undefined) {
      return `${limit.currentUsage}`;
    }
  }

  return undefined;
};

export const AppFeaturePlan: React.FC<IAppFeaturePlanProps> = (props) => {
  const { sx, appId } = props;
  const { t, i18n } = useTranslation();

  const currentFeaturePlanQuery = useAppCurrentFeaturePlanQuery({
    appId,
  });
  const plan = currentFeaturePlanQuery.data;

  return (
    <ContentBlock sx={sx}>
      <ContentBlock.Suspense
        loading={currentFeaturePlanQuery.isLoading}
        error={currentFeaturePlanQuery.error}
        first
      >
        <ContentBlock.Title>
          {t('AppFeaturePlan.title', { name: plan?.name[i18n.language] })}
        </ContentBlock.Title>
        <ContentBlock.InfoList>
          {plan?.features
            ? Object.entries(plan.features).map(([name, limit]) => (
                <ContentBlock.InfoListItem
                  key={name}
                  label={t(`AppFeaturePlan.features.${name}.label`)}
                >
                  {limit ? (
                    <Stack horizontal gap={2}>
                      <FontAwesomeIcon
                        icon={faLockOpen}
                        {...stylex.props(styles.unlocked)}
                      />
                      {typeof limit === 'boolean'
                        ? null
                        : getFormattedLimit(limit)}
                    </Stack>
                  ) : (
                    <FontAwesomeIcon
                      icon={faLock}
                      {...stylex.props(styles.locked)}
                    />
                  )}
                </ContentBlock.InfoListItem>
              ))
            : null}
        </ContentBlock.InfoList>
      </ContentBlock.Suspense>
    </ContentBlock>
  );
};
